import React from 'react';
import { format, parseISO } from 'date-fns';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ConsultationHistory = ({ consultations, totalCount, onViewAll, fullView = false }) => {
  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'confirmed': 'bg-teal-100 text-teal-800 border-teal-200',
      'completed': 'bg-green-100 text-green-800 border-green-200',
      'cancelled': 'bg-red-100 text-red-800 border-red-200'
    };
    return colors?.[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'pending': 'Clock',
      'confirmed': 'CheckCircle',
      'completed': 'CheckCheck',
      'cancelled': 'XCircle'
    };
    return icons?.[status] || 'Circle';
  };

  const formatDateTime = (dateString) => {
    try {
      return format(parseISO(dateString), 'MMM dd, yyyy • h:mm a');
    } catch {
      return 'Date not available';
    }
  };

  if (!consultations || consultations?.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-elevation-1 p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-[#002B45]">Consultation History</h3>
        </div>
        <div className="text-center py-12">
          <Icon name="Calendar" size={48} className="text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No consultations yet</p>
          <p className="text-sm text-gray-400 mt-2">Your consultation history will appear here</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-elevation-1 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
            <Icon name="Calendar" size={20} className="text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-[#002B45]">Consultation History</h3>
            <p className="text-sm text-gray-500">{totalCount} total sessions</p>
          </div>
        </div>
        {!fullView && onViewAll && (
          <Button
            onClick={onViewAll}
            variant="outline"
            size="sm"
            iconName="ArrowRight"
            iconPosition="right"
          >
            View All
          </Button>
        )}
      </div>

      <div className="space-y-4">
        {consultations?.map((consultation) => (
          <div
            key={consultation?.id}
            className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-300"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <h4 className="font-semibold text-[#002B45] mb-1">{consultation?.title}</h4>
                <p className="text-sm text-gray-600 mb-2">
                  {formatDateTime(consultation?.scheduledStart)}
                </p>
                {consultation?.serviceInterest && (
                  <p className="text-xs text-gray-500">
                    Service: {consultation?.serviceInterest}
                  </p>
                )}
              </div>
              <span
                className={`px-3 py-1 rounded-full text-xs font-medium border flex items-center gap-1 ${getStatusColor(
                  consultation?.status
                )}`}
              >
                <Icon name={getStatusIcon(consultation?.status)} size={12} />
                {consultation?.status}
              </span>
            </div>

            {consultation?.sessionNotes && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-xs font-semibold text-blue-900 mb-2 flex items-center gap-1">
                    <Icon name="FileText" size={14} />
                    Session Summary
                  </p>
                  <p className="text-sm text-gray-700 line-clamp-2">
                    {consultation?.sessionNotes?.sessionSummary}
                  </p>
                  {consultation?.sessionNotes?.actionItems?.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs font-semibold text-blue-900 mb-1">Action Items:</p>
                      <ul className="space-y-1">
                        {consultation?.sessionNotes?.actionItems?.slice(0, 2)?.map((item, idx) => (
                          <li key={idx} className="text-xs text-gray-600 flex items-start gap-2">
                            <Icon name="CheckSquare" size={12} className="mt-0.5 flex-shrink-0" />
                            <span>{item?.item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {fullView && consultation?.description && (
              <p className="text-sm text-gray-600 mt-3">{consultation?.description}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ConsultationHistory;